import { useEffect, useMemo, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import cn from 'classnames'
import {
   Button,
   ContentCard,
   Grid,
   Select,
   Stack,
   TextInput,
} from '@forwardflow/ui-v2'
import {
   ArrowLeftMdIcon,
   ChevronRightIcon,
   PhoneIcon,
   SearchMagnifyingGlassIcon,
   StopCircle04Icon,
} from '@forwardflow/icons'
import { useAppStore } from '@forwardflow/store/appStore'
import { IStage, useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { IStageMapGroup } from '@forwardflow/types/onboarding'
import { initialStageMappingsGroups } from '@forwardflow/constants/onboarding.constants'
import {
   useGetFubStages,
   useGetPeopleTags,
   useGetSuggestedStageMappings,
   useSearchAvailableAreaCodes,
} from '@forwardflow/queries/onboarding.queries'
import {
   DEMO_STAGES,
   DEMO_SUGGESTIONS,
   DEMO_TAGS,
   DEV_DUMMY,
   isFreeFlow,
   useDevTools,
} from '../dev/devTools'
import {
   addStop,
   applyAllSuggestions,
   hasAnySelection,
   itemKey,
   joinNames,
   removeStop,
   stopItems,
   suggestedStops,
   TMappedItem,
} from './stage-mapping-explained'
import { TBucketKey } from './stage-mapping-explained/buckets'

const MAX_AREA_CODES = 3
const STOP_COLOR = '#FF4B4B'

/** The three-digit US area code of a phone number, if it has one. */
const areaCodeOf = (phone?: string | null): string | null => {
   const digits = (phone || '').replace(/\D/g, '')
   const national =
      digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
   return national.length === 10 ? national.slice(0, 3) : null
}

const Chips = ({ n = 3 }: { n?: number }) => (
   <div className="flex flex-wrap gap-2">
      {[...Array(n)].map((_, i) => (
         <div key={i} className="rounded-[8px] bg-white/10 animate-pulse h-8 w-24" />
      ))}
   </div>
)

/**
 * Step 4: two confirmations on one screen.
 *
 * The calling number: an area code taken from the owner's Follow Up Boss
 * phone (then the team's), availability-checked and pre-selected. "Change"
 * opens the team's codes and a three-digit search. Numbers themselves are
 * reserved when the campaign launches; the customer never sees a list of
 * numbers.
 *
 * The stop rules: Forward Flow's suggested stop stages and tags, pre-applied
 * and summarised in one sentence. "Edit" opens the same chip editor the
 * stage mapping screen used. Saved in the same `stageMappings` shape.
 */
export const NumberAndStops = () => {
   const { currentOrganization } = useAppStore()
   const {
      onboardingData,
      getNextStep,
      getPreviousStep,
      setCampaignConfiguration,
      setStageMappings,
   } = useOnboardingStoreV2()
   const orgId = onboardingData.organizationId || currentOrganization || ''
   const freeFlow = isFreeFlow()

   /* ---------- Area code ---------- */

   const selectedCodes = useMemo(
      () => onboardingData.campaignConfiguration?.area_codes || [],
      [onboardingData.campaignConfiguration?.area_codes]
   )

   const teamCodes = useMemo(() => {
      const users = [...(onboardingData.invitedUsers || [])].sort(
         (a, b) => Number(b.isOwner) - Number(a.isOwner)
      )
      const seen = new Set<string>()
      const out: { code: string; name: string; isOwner: boolean }[] = []
      users.forEach((u) => {
         const code = areaCodeOf(u.phone)
         if (!code || seen.has(code)) return
         seen.add(code)
         out.push({
            code,
            name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
            isOwner: Boolean(u.isOwner),
         })
      })
      return freeFlow && out.length === 0
         ? DEV_DUMMY.areaCodes.map((code, i) => ({
              code,
              name: i === 0 ? 'you' : 'your team',
              isOwner: i === 0,
           }))
         : out.slice(0, MAX_AREA_CODES)
   }, [onboardingData.invitedUsers, freeFlow])

   const check0 = useSearchAvailableAreaCodes(teamCodes[0]?.code || '')
   const check1 = useSearchAvailableAreaCodes(teamCodes[1]?.code || '')
   const check2 = useSearchAvailableAreaCodes(teamCodes[2]?.code || '')
   const checks = [check0, check1, check2]
   const suggestions = teamCodes.map((t, i) => {
      const list: number[] | undefined = checks[i].data?.areaCodes
      const available = freeFlow
         ? true
         : Array.isArray(list)
           ? list.map(String).includes(t.code)
           : undefined
      return { ...t, available, isChecking: checks[i].isLoading }
   })
   const isSuggesting = suggestions.some((s) => s.isChecking)
   const suggestedReady = suggestions
      .filter((s) => s.available === true)
      .map((s) => s.code)

   // Pre-select the owner's code once, if nothing is chosen yet.
   const preselected = useRef(false)
   useEffect(() => {
      if (preselected.current || isSuggesting || selectedCodes.length) return
      if (!suggestedReady.length) return
      preselected.current = true
      setCampaignConfiguration({ area_codes: [suggestedReady[0]] })
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isSuggesting, suggestedReady.join(',')])

   const [changingNumber, setChangingNumber] = useState(false)
   const [codeSearch, setCodeSearch] = useState('')
   const searched = codeSearch.replace(/\D/g, '').slice(0, 3)
   const { data: searchedCodes, isLoading: isSearching } =
      useSearchAvailableAreaCodes(searched.length === 3 ? searched : '')

   const toggleCode = (code: string) => {
      if (selectedCodes.includes(code)) {
         setCampaignConfiguration({
            area_codes: selectedCodes.filter((c) => c !== code),
         })
      } else if (selectedCodes.length < MAX_AREA_CODES) {
         setCampaignConfiguration({ area_codes: [...selectedCodes, code] })
      }
   }

   const ownerCode = suggestions.find((s) => s.isOwner)?.code
   const numberSentence = selectedCodes.length
      ? `Your calls come from ${selectedCodes.length === 1 ? 'a' : ''} ${selectedCodes
           .map((c) => `(${c})`)
           .join(', ')} number${selectedCodes.length > 1 ? 's' : ''}`
      : isSuggesting
        ? 'Finding a local number for you'
        : "We'll pick a local number for you"
   const numberWhy =
      selectedCodes.length === 1 && selectedCodes[0] === ownerCode
         ? 'Matches your own phone, so leads see a local caller.'
         : selectedCodes.length
           ? 'Leads see a local caller.'
           : 'No phone numbers on your team in Follow Up Boss yet. Pick an area code or let us choose.'

   /* ---------- Stop rules ---------- */

   const { data: fubStages, isLoading: isLoadingStages } = useGetFubStages(
      orgId,
      10000,
      1,
      true
   )
   const { data: fubTags, isLoading: isLoadingTags } = useGetPeopleTags(
      orgId,
      1,
      10000,
      true
   )

   const stages: IStage[] = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({
               id: String(s.stageId),
               name: s.stageName,
               description: '',
               type: 'stage' as const,
            })) || [],
      [fubStages]
   )
   const tags: IStage[] = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t) => ({
               id: t.tagName,
               name: t.tagName,
               description: '',
               type: 'tag' as const,
            })) || [],
      [fubTags]
   )
   const stagesForSuggestion = useMemo(
      () =>
         fubStages?.data
            ?.filter((s) => s?.stageId && s?.stageName?.trim())
            .map((s) => ({ stageId: s.stageId, stageName: s.stageName })) || [],
      [fubStages]
   )
   const tagsForSuggestion = useMemo(
      () =>
         fubTags?.data
            ?.filter((t) => t?.tagName?.trim())
            .map((t, index) => ({ tagId: index + 1, tagName: t.tagName })) || [],
      [fubTags]
   )
   const { data: suggested, isLoading: isLoadingSuggestions } =
      useGetSuggestedStageMappings(
         stagesForSuggestion,
         tagsForSuggestion,
         !isLoadingStages && !isLoadingTags
      )

   const groups: IStageMapGroup[] = onboardingData.stageMappings?.length
      ? onboardingData.stageMappings
      : initialStageMappingsGroups

   const useDemo =
      freeFlow && !isLoadingStages && !isLoadingTags && stages.length === 0
   const stagesShown = useDemo ? DEMO_STAGES : stages
   const tagsShown = useDemo ? DEMO_TAGS : tags
   const suggestionsShown = useDemo ? DEMO_SUGGESTIONS : suggested?.data
   const isLoadingOptions = !useDemo && (isLoadingStages || isLoadingTags)
   const isWaitingForSuggestions =
      !useDemo &&
      (isLoadingSuggestions ||
         (!suggested &&
            !isLoadingStages &&
            !isLoadingTags &&
            (stagesForSuggestion.length > 0 || tagsForSuggestion.length > 0)))

   // Pre-fill the suggestions once, only on a mapping nobody has touched.
   const prefilled = useRef(false)
   useEffect(() => {
      if (prefilled.current || !suggestionsShown?.length) return
      prefilled.current = true
      if (hasAnySelection(groups)) return
      setStageMappings(applyAllSuggestions(groups, suggestionsShown))
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [suggestionsShown])

   const stops = useMemo(() => stopItems(groups), [groups])
   const moreSuggested = useMemo(
      () => suggestedStops(suggestionsShown, groups),
      [suggestionsShown, groups]
   )
   const [editingStops, setEditingStops] = useState(false)
   const [pickCount, setPickCount] = useState(0)
   const chosen = useMemo(() => new Set(stops.map(itemKey)), [stops])
   const options = useMemo(
      () =>
         [
            {
               group: 'Stages',
               items: stagesShown
                  .filter((s) => !chosen.has(itemKey({ id: s.id, type: 'stage' })))
                  .map((s) => ({ value: `stage:${s.id}`, label: s.name })),
            },
            {
               group: 'Tags',
               items: tagsShown
                  .filter((t) => !chosen.has(itemKey({ id: t.id, type: 'tag' })))
                  .map((t) => ({ value: `tag:${t.id}`, label: t.name })),
            },
         ].filter((g) => g.items.length > 0),
      [stagesShown, tagsShown, chosen]
   )
   const add = (item: TMappedItem, bucket?: TBucketKey) => {
      setStageMappings(addStop(groups, item, bucket))
      setPickCount((n) => n + 1)
   }
   const pick = (value: string | null) => {
      if (!value) return
      const [type, ...rest] = value.split(':')
      const id = rest.join(':')
      const source = type === 'tag' ? tagsShown : stagesShown
      const found = source.find((s) => String(s.id) === id)
      if (found) add({ ...found, type: type as 'stage' | 'tag' })
   }

   /* ---------- Continue ---------- */

   const stopsReady = freeFlow || stops.length > 0
   const canContinue = stopsReady && !isLoadingOptions
   const buttonLabel = isLoadingOptions
      ? 'Reading your stages'
      : !stopsReady
        ? 'Pick at least one stop stage'
        : 'Looks right'

   const handleContinue = () => {
      setStageMappings(groups)
      getNextStep()
   }

   // Dev tools (hidden bar): the three Colorado codes and every suggestion.
   useDevTools({
      fill: () => {
         setCampaignConfiguration({ area_codes: DEV_DUMMY.areaCodes.slice(0, 1) })
         if (suggestionsShown) {
            setStageMappings(applyAllSuggestions(groups, suggestionsShown))
         }
      },
      skip: () => {
         if (!selectedCodes.length) {
            setCampaignConfiguration({ area_codes: DEV_DUMMY.areaCodes.slice(0, 1) })
         }
         getNextStep()
         return true
      },
   })

   const codeChip = (code: string, extra?: string, unavailable?: boolean) => {
      const isSelected = selectedCodes.includes(code)
      const full = !isSelected && selectedCodes.length >= MAX_AREA_CODES
      return (
         <button
            key={code}
            type="button"
            disabled={full || unavailable}
            title={unavailable ? 'No numbers available in this area code right now' : undefined}
            onClick={() => toggleCode(code)}
            className={cn(
               'rounded-lg border px-4 py-2 text-sm transition-all',
               isSelected
                  ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.30)] text-white'
                  : full || unavailable
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed'
                    : 'border-white/20 bg-white/5 text-white hover:border-[#9F7BFF]/50 hover:bg-[rgba(159,123,255,0.10)]'
            )}
         >
            ({code})
            {extra && <span className="text-white/50 ml-2 text-xs">{extra}</span>}
            {unavailable && <span className="text-white/40 ml-2 text-xs">unavailable</span>}
         </button>
      )
   }

   return (
      <Stack gap={24}>
         {/* 1. Number */}
         <ContentCard
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="number-card"
         >
            <div className="flex items-start gap-4">
               <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-[8px] border border-white/10 bg-[#9F7BFF]/10 text-[#A684FF]">
                  <PhoneIcon width={20} height={20} />
               </span>
               <div className="flex-1 min-w-0">
                  {isSuggesting && !selectedCodes.length ? (
                     <div className="h-6 w-64 rounded bg-white/10 animate-pulse" />
                  ) : (
                     <p className="text-white font-semibold text-lg">{numberSentence}</p>
                  )}
                  <p className="text-[#99A1AF] text-sm font-medium mt-1">{numberWhy}</p>
               </div>
               <button
                  type="button"
                  className="text-[#A684FF] text-sm font-medium hover:underline shrink-0"
                  onClick={() => setChangingNumber((v) => !v)}
               >
                  {changingNumber ? 'Done' : 'Change'}
               </button>
            </div>

            {changingNumber && (
               <div className="space-y-4 border-t border-white/10 pt-4">
                  <div className="space-y-2">
                     <p className="text-white/90 text-sm font-medium">From your team's phones</p>
                     {isSuggesting ? (
                        <Chips />
                     ) : suggestions.length ? (
                        <div className="flex flex-wrap gap-2.5">
                           {suggestions.map((s) =>
                              codeChip(
                                 s.code,
                                 s.isOwner ? 'your number' : s.name,
                                 s.available === false
                              )
                           )}
                        </div>
                     ) : (
                        <p className="text-white/40 text-sm">
                           No phone numbers on your Follow Up Boss team yet.
                        </p>
                     )}
                  </div>
                  <TextInput
                     label="Or any area code"
                     leftSection={<SearchMagnifyingGlassIcon width={16} />}
                     placeholder="3 digits, e.g. 616"
                     value={codeSearch}
                     onChange={(e) =>
                        setCodeSearch(e.currentTarget.value.replace(/\D/g, '').slice(0, 3))
                     }
                     inputMode="numeric"
                     maxLength={3}
                  />
                  {searched.length === 3 && (
                     <div className="flex flex-wrap gap-2.5">
                        {isSearching ? (
                           <Chips n={1} />
                        ) : searchedCodes?.areaCodes?.length ? (
                           searchedCodes.areaCodes.map((c: number) => codeChip(String(c)))
                        ) : (
                           <p className="text-white/40 text-sm">
                              No numbers available in ({searched}) right now.
                           </p>
                        )}
                     </div>
                  )}
                  <p className="text-white/40 text-xs">
                     Up to {MAX_AREA_CODES} area codes. First 3 numbers are on us.
                  </p>
               </div>
            )}
         </ContentCard>

         {/* 2. Stop rules */}
         <ContentCard
            className="space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
            data-testid="stops-card"
         >
            <div className="flex items-start gap-4">
               <span
                  className="flex shrink-0 items-center justify-center w-10 h-10 rounded-[8px] border border-white/10"
                  style={{ color: STOP_COLOR, background: `${STOP_COLOR}1A` }}
               >
                  <StopCircle04Icon width={22} height={22} />
               </span>
               <div className="flex-1 min-w-0">
                  {isLoadingOptions || (isWaitingForSuggestions && !stops.length) ? (
                     <div className="h-6 w-80 max-w-full rounded bg-white/10 animate-pulse" />
                  ) : stops.length ? (
                     <p className="text-white font-semibold text-lg">
                        We stop calling once a lead reaches{' '}
                        <span className="text-white">{joinNames(stops)}</span>
                     </p>
                  ) : (
                     <p className="text-white font-semibold text-lg">
                        Tell us when to stop calling a lead
                     </p>
                  )}
                  <p className="text-[#99A1AF] text-sm font-medium mt-1">
                     Taken from your Follow Up Boss stages and tags. Everything
                     else gets worked and tagged ff-nurturing; a booked lead gets
                     ff-appointment-set and you take over.
                  </p>
               </div>
               <button
                  type="button"
                  className="text-[#A684FF] text-sm font-medium hover:underline shrink-0"
                  onClick={() => setEditingStops((v) => !v)}
               >
                  {editingStops ? 'Done' : 'Edit'}
               </button>
            </div>

            {editingStops && (
               <div className="space-y-3 border-t border-white/10 pt-4" data-bucket="ff-stopped">
                  <p className="text-white/90 text-sm font-medium">
                     We stop on these stages and tags
                  </p>
                  {isLoadingOptions ? (
                     <Chips />
                  ) : stops.length ? (
                     <div className="flex flex-wrap gap-2">
                        {stops.map((item) => (
                           <span
                              key={itemKey(item)}
                              className="flex items-center gap-2 rounded-[8px] bg-white/20 py-1.5 pl-3 pr-2 text-sm text-white"
                              title={item.bucketTitle}
                           >
                              <span className="text-white/50 text-[10px] uppercase tracking-wide">
                                 {item.type}
                              </span>
                              {item.name}
                              <button
                                 type="button"
                                 aria-label={`Remove ${item.name}`}
                                 className="ml-1 w-4 h-4 rounded-full bg-white/10 hover:bg-red-500 text-white/70 hover:text-white text-[11px] leading-none flex items-center justify-center transition-colors"
                                 onClick={() => setStageMappings(removeStop(groups, item))}
                              >
                                 ×
                              </button>
                           </span>
                        ))}
                     </div>
                  ) : (
                     <p className="text-white/40 text-sm">
                        Nothing yet. Pick at least one stage or tag below.
                     </p>
                  )}
                  {isWaitingForSuggestions ? (
                     <div className="flex items-center gap-2">
                        <span className="text-white/50 text-xs">Suggested</span>
                        <Chips n={2} />
                     </div>
                  ) : moreSuggested.length > 0 ? (
                     <div className="flex flex-wrap items-center gap-2">
                        <span className="text-white/50 text-xs">Suggested</span>
                        {moreSuggested.map((item) => (
                           <button
                              key={itemKey(item)}
                              type="button"
                              title={item.bucketTitle}
                              className="flex items-center gap-1.5 rounded-[8px] bg-primary/80 hover:bg-primary text-white py-1 px-3 text-sm transition-colors"
                              onClick={() => add(item, item.bucket)}
                           >
                              + {item.name}
                              <span className="text-white/60 text-[10px] uppercase">{item.type}</span>
                           </button>
                        ))}
                     </div>
                  ) : null}
                  <Select
                     key={`stop-${pickCount}`}
                     searchable
                     value={null}
                     label="Add a stage or tag we should not touch"
                     placeholder="Search your Follow Up Boss stages and tags"
                     data={options}
                     onChange={pick}
                     disabled={isLoadingOptions}
                     nothingFoundMessage="Nothing in Follow Up Boss matches"
                     comboboxProps={{ withinPortal: true }}
                  />
                  <p className="text-white/40 text-xs">
                     Forward Flow never changes any other stage. Edit this any
                     time under Settings → Stage Mapping.
                  </p>
               </div>
            )}
         </ContentCard>

         <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
            className="space-y-2"
         >
            <Grid>
               <Grid.Col span={3}>
                  <Button
                     variant="secondary"
                     className="w-full"
                     onClick={() => getPreviousStep()}
                  >
                     <ArrowLeftMdIcon width={12} /> Back
                  </Button>
               </Grid.Col>
               <Grid.Col span={9}>
                  <Button
                     className="w-full"
                     onClick={handleContinue}
                     disabled={!canContinue}
                  >
                     {buttonLabel} <ChevronRightIcon width={18} />
                  </Button>
               </Grid.Col>
            </Grid>
            <p className="text-center text-xs text-white/40">
               Next: pick who we call first.
            </p>
         </motion.div>
      </Stack>
   )
}
